/**
 * Typed wrappers for the platform auth API.
 *
 *   POST /api/auth/login                 → { redirect_to }
 *   GET  /api/auth/register/check        → mode + invitation + client
 *   POST /api/auth/register              → { redirect_to }
 *   POST /api/auth/reset-request         → { sent: true } (always)
 *   GET  /api/auth/reset-password/check  → { valid, reason? }
 *   POST /api/auth/reset-password        → { redirect_to }
 *
 * Server-supplied `redirect_to` is the source of truth for navigation
 * (the server knows about memberships, super-admin status, etc.).
 * SPA does a full-page nav so the next request carries the freshly-
 * set session cookie AND the destination handler - which might be
 * Jinja or another SPA - sees a logged-in user.
 */
import { api } from './client';

// ── Login ───────────────────────────────────────────────────────────

export interface LoginRequest {
  email: string;
  password: string;
  remember?: boolean;
}

export interface LoginResponse {
  /** Where to navigate after login. Always set on a 200. Always
   *  same-origin - server sanitises. */
  redirect_to: string;
}

export function login(req: LoginRequest): Promise<LoginResponse> {
  return api<LoginResponse>('/api/auth/login', {
    method: 'POST',
    body: req,
  });
}

// ── Registration ────────────────────────────────────────────────────

export type RegisterMode = 'invitation' | 'initial_setup';

export interface RegisterCheckResponse {
  mode: RegisterMode;
  invitation?: {
    email: string;
    role: 'admin' | 'user' | string;
  };
  client?: {
    name: string;
    display_name: string;
    logo_url: string;
  } | null;
}

export function checkRegister(token: string): Promise<RegisterCheckResponse> {
  const qs = token ? `?token=${encodeURIComponent(token)}` : '';
  return api<RegisterCheckResponse>(`/api/auth/register/check${qs}`);
}

export interface RegisterRequest {
  token?: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  organisation?: string;
  password: string;
}

export function register(req: RegisterRequest): Promise<LoginResponse> {
  return api<LoginResponse>('/api/auth/register', {
    method: 'POST',
    body: req,
  });
}

// ── Password reset ──────────────────────────────────────────────────

export function resetRequest(email: string): Promise<{ sent: boolean }> {
  return api<{ sent: boolean }>('/api/auth/reset-request', {
    method: 'POST',
    body: { email },
  });
}

export interface ResetTokenCheckResponse {
  valid: boolean;
  reason?: 'expired' | 'invalid';
}

export function checkResetToken(token: string): Promise<ResetTokenCheckResponse> {
  return api<ResetTokenCheckResponse>(
    `/api/auth/reset-password/check?token=${encodeURIComponent(token)}`,
  );
}

export function resetPassword(
  token: string,
  password: string,
): Promise<LoginResponse> {
  return api<LoginResponse>('/api/auth/reset-password', {
    method: 'POST',
    body: { token, password },
  });
}

// ── Select-client (post-login chooser) ──────────────────────────────

export interface SelectableClient {
  client_id: string;
  name: string;
  role: 'admin' | 'user' | string;
  /** Empty string if the client hasn't uploaded a logo. */
  logo_url: string;
}

export interface SelectClientOptionsResponse {
  clients: SelectableClient[];
}

export function selectClientOptions(): Promise<SelectClientOptionsResponse> {
  return api<SelectClientOptionsResponse>('/api/auth/select-client/options');
}

export function selectClient(clientId: string): Promise<LoginResponse> {
  return api<LoginResponse>('/api/auth/select-client', {
    method: 'POST',
    body: { client_id: clientId },
  });
}
